import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { ReactComponent as Print } from "../../../../../assets/ViewLeave-images/print.svg";
import { formatDate } from "../../../../../utils/DateFormat";
import { ReactComponent as Download } from "../../../../../assets/invoice/blueDownload.svg";
import CustomInput from "../../../../../components/CustomInput";
import Modal from "../../../../../components/Modal";
import { payslipData } from "../../../../../constants/data";
import { ItemsValidates } from "../../../../../helper/Validation";
import { useForm } from "../../../../../hooks/useForm";
import { getTaxLabel, getVisibleTaxSetting } from "../../../../../utils/payslipSettings";
import style from "./style";
import { getDateFormat } from "../../../../../utils/dateFormatStore";

const PayslipPage = ({
  getLoanList,
  addLeave,
  getLeave,
  leaveData,
  addDeduction,
  getDeductionList,
  deductionData,
  loanData,
  addLevie,
  handleLoanPayslip,
  handleUpdate,
  loading,
  salaryData,
  getAllowanceList,
  getLevieList,
  allowanceData,
  levieData,
  getFringeList,
  fringeData,
  payslip,
  getContribution,
  contribution,
  getLeaveType,
  deductionType,
  leviesType,
  handleExportPayslip
}) => {
  const [pensionOpen, setPensionOpen] = useState(false);
  const [allowanceOpen, setAllowanceOpen] = useState(false);
  const [levieOpen, setLevieOpen] = useState(false);
  const [fringeOpen, setFringeOpen] = useState(false);
  const [loanOpen, setLoanOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deductionOpen, setDeductionOpen] = useState(false);
  const [deduction, setDeduction] = useState(
    deductionData?.deductions || [{ type: "", rateValue: "" }]
  );
  const [travelValue, setTravelValue] = useState(salaryData?.travelAllowanceKilometer);
  const [travelReimbursementValue, setTravelReimbursementValue] = useState(salaryData?.travelReimbursementKilometer);
  const [travelValueWorking, setTravelValueWorking] = useState(salaryData?.travelAllowanceKilometer);
  const [calculatedTotal, setCalculatedTotal] = useState();
  const [calculatedTotalWorking, setCalculatedTotalWorking] = useState();
  const [leaveFrom, setLeaveFrom] = useState();
  const [leaveTo, setLeaveTo] = useState();
  const [leaveType, setLeaveType] = useState("");
  const [levies, setLevies] = useState([]);
  const [loans, setLoans] = useState([]);
  const [hasLoanChanges, setHasLoanChanges] = useState(false);
  const [initialLoanValues, setInitialLoanValues] = useState([]);

  useEffect(() => {
    if (loanData?.length) {
      const initialLoans = loanData.map((item) => ({
        loanTrans: item._id || item.loanTrans,
        loanAmt: item.loanAmt || 0,
        repayAmt: item.repayAmt || 0,
        adjustedValue: item.repayAmt || 0 // Store the original value
      }));

      setLoans(initialLoans);
      setInitialLoanValues(initialLoans.map(item => item.adjustedValue));
      setHasLoanChanges(false);
    } else {
      setLoans([]);
      setInitialLoanValues([]);
      setHasLoanChanges(false);
    }
  }, [loanData, salaryData]);

  useEffect(() => {
    if (levieData?.levies) {
      setLevies(
        levieData.levies.map((item) => ({
          _id: item._id,
          type: item.type,
          rateValue: item.employeeCont?.employerContAmount || "" // Map the API value to rateValue
        }))
      );
    } else if (levieData?.length) {
      setLevies(levieData);
    } else {
      setLevies([{ type: "", rateValue: "" }]);
    }
  }, [levieData]);

  useEffect(() => {
    if (deductionData?.deductions?.length) {
      setDeduction(deductionData?.deductions);
    } else {
      setDeduction([{ type: "", rateValue: "" }]);
    }
  }, [deductionData]);

  const handleLoanChange = (value, index, key) => {
    const updatedLoans = [...loans];

    if (value === "") {
      updatedLoans[index][key] = "";
      setLoans(updatedLoans);
      setHasLoanChanges(false); // Empty input shouldn't enable save
      return;
    }

    let newValue = Number(value);
    if (isNaN(newValue)) {
      return; // Ignore non-numeric inputs
    }

    if (value.startsWith("0") && value !== "0") {
      return; // Reject inputs like "01", "012"
    }

    if (newValue < 1) {
      newValue = 1;
    }

    const maxValue = Number(loans[index]?.loanAmt) || 0;
    if (newValue > maxValue) {
      newValue = maxValue;
    }

    updatedLoans[index][key] = newValue;
    setLoans(updatedLoans);

    const hasChanges = updatedLoans.some((loan, i) =>
      loan.adjustedValue !== initialLoanValues[i]
    );
    setHasLoanChanges(hasChanges);
  };

  const handleLoanSubmit = () => {
    const payload = loans
      .filter((item) => item.adjustedValue !== "" && Number(item.adjustedValue) >= 1)
      .map((item) => ({
        repayAmt: Number(item.adjustedValue)
      }));
    handleLoanPayslip(salaryData?._id, salaryData?.payroll?._id, salaryData?.employee?._id, payload, loanData?.[0]?._id);
    setLoanOpen(false);
  };

  useEffect(() => {
    if (loanData?.length) {
      setLoans(loanData);
    } else {
      setLoans([{ adjustedValue: "" }]);
    }
  }, [loanData]);

  const handleLevieChange = (value, index, key) => {
    const updatedLevies = [...levies];
    updatedLevies[index][key] = key === "rateValue" ? Number(value) || "" : value;
    setLevies(updatedLevies);

    setLevies(updatedLevies);
  };

  const handleLevieSubmit = () => {
    const changedLevies = levies
      .filter((item) => item.type && item.rateValue && Number(item.rateValue) > 0)
      .map((item) => ({
        levyTrans: item._id,
        employee: salaryData?.employee?._id,
        salaryId: salaryData?._id,
        rateValue: item.rateValue, // Send rateValue as is
      }));
    addLevie(
      salaryData?._id,
      salaryData?.payroll?._id,
      salaryData?.employee?._id,
      changedLevies
    );
    setLevieOpen(false);
  };


  useEffect(() => {
    if (levieData?.length) {
      setLevies(levieData);
    } else {
      setLevies([{ type: "", rateValue: "" }]);
    }
  }, [levieData]);

  const handleDeleteDeduction = (index) => {
    const updatedProductInformation = [...deduction];
    updatedProductInformation.splice(index, 1);
    setDeduction(updatedProductInformation);
  };

  const handleDeductionChange = (value, index, key) => {
    const updatedDeduction = [...deduction];
    updatedDeduction[index][key] =
      key === "rateValue" ? Number(value) : value;
    setDeduction(updatedDeduction);
  };

  const handleAddDeduction = () => {
    setDeduction((prevDeduction) => [
      ...prevDeduction,
      { type: "", rateValue: "" },
    ]);
  };

  const handleLeaveData = () => {
    addLeave(
      salaryData?._id,
      leaveFrom ? dayjs(leaveFrom).format("YYYY-MM-DD") : null,
      leaveTo ? dayjs(leaveTo).format("YYYY-MM-DD") : null,
      leaveType,
      salaryData?.payroll?._id,
      salaryData?.employee?._id
    );
    setLeaveOpen(false);
  };

  const updateDeduction = () => {
    addDeduction(salaryData?._id, salaryData?.payroll?._id, salaryData?.employee?._id, deduction, levieData?._id);
    setDeductionOpen(false);
  };

  const handleData = (user) => {
    handleUpdate(user, travelValue, travelValueWorking);
  };
  const handleLeave = () => {
    getLeave();
    setLeaveOpen(true);
  };
  const handlePension = () => {
    getContribution();
    setPensionOpen(true);
  };
  const handleLoan = () => {
    try {
      getLoanList();
      setLoanOpen(true);
    } catch (error) {
      setLoanOpen(true); // Still try to open the modal
    }
  };

  const handleDeduction = () => {
    getDeductionList();
    setDeductionOpen(true);
  };

  const handleAllowance = () => {
    getAllowanceList();
    setAllowanceOpen(true);
  };
  const handleFringe = () => {
    getFringeList();
    setFringeOpen(true);
  };
  const handleLevie = () => {
    getLevieList();
    setLevieOpen(true);
  };
  const { state, handleChange, handleSubmit, handleFocus } = useForm(
    payslipData,
    ItemsValidates,
    handleData
  );
  const {
    employeeName,
    employeeNumber,
    salaryPHr,
    restraintTrade,
    reimbursiveRate,
    allowances,
    commission,
    taxable,
    totalTax,
    fringeBenefit,
    allDeductions,
    loanRepayment,
    uif,
    employerLevies,
    leave,
    grossIncome,
    totalDeductions,
    nettPay,
    payable,
    isShow,
  } = state;

  // UIF/SDL rows follow the country-driven /payslip_settings list; labels come from the backend.
  const uifSetting = getVisibleTaxSetting(payslip, "UIF");
  const sdlSetting = getVisibleTaxSetting(payslip, "SDL");

  const handleChanges = (value) => {
    const rateRembTravel = salaryData?.employee?.salaryInfo?.rateRembTravel || 0;
    const kmTotal = Math.max(0, Number(value));
    // Update travel value
    setTravelValue(value);
    const total = Math.max(0, kmTotal * rateRembTravel);
    // Calculate the total directly
    setCalculatedTotal(total);
  };

  const handleChangesWorking = (value) => {
    const calHourlySalary = salaryData?.calHourlySalary?.calHourlySalary || 0;

    setTravelValueWorking(value);

    const totalWorking = Number(value) * calHourlySalary;
    setCalculatedTotalWorking(totalWorking);
  };

  return (
    <>
      <Box sx={style.mainContainer}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Payslip</Typography>
          <Button
            sx={style.exportBtn}
            onClick={() => handleExportPayslip(salaryData?._id)}
          >
            <Download />
            Export
          </Button>
        </Box>
      </Box>
      <form onSubmit={(e) => handleSubmit(e)}>
        <Grid container>
          <Grid item xs={12} lg={11.6} sx={style.gridbox}>
            <Box sx={style.inputSections}>
              <Box sx={style.inputBoxes}>
                <Typography sx={style.inputText}>Employee Name</Typography>
                <CustomInput
                  variant="outlined"
                  required
                  fullWidth
                  readOnly={true}
                  id="employeeName"
                  error={employeeName?.error}
                  name="employeeName"
                  defaultValue={salaryData?.fullName}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  autoComplete="employeeName"
                />
              </Box>
              <Box sx={style.inputBoxes}>
                <Typography sx={style.inputText}>Staff Id</Typography>
                <CustomInput
                  variant="outlined"
                  required
                  fullWidth
                  id="employeeNumber"
                  error={employeeNumber?.error}
                  name="employeeNumber"
                  defaultValue={salaryData?.employee?.stafId ?? 0}
                  readOnly={true}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  autoComplete="employeeNumber"
                />
              </Box>
              <Box sx={style.inputBoxes}>
                <Typography sx={style.inputText}>Company Name</Typography>
                <CustomInput
                  variant="outlined"
                  required
                  fullWidth
                  readOnly={true}
                  id="employeeName"
                  error={employeeName?.error}
                  name="employeeName"
                  defaultValue={salaryData?.company?.companyName}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  autoComplete="employeeName"
                />
              </Box>
            </Box>

            <Box sx={style.inputSections}>
              <Box sx={style.inputBoxes}>
                <Typography sx={style.inputText}>Payslip Number</Typography>
                <CustomInput
                  variant="outlined"
                  required
                  fullWidth
                  readOnly={true}
                  id="employeeName"
                  error={employeeName?.error}
                  name="employeeName"
                  defaultValue={salaryData?.payslip_number_alias}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  autoComplete="employeeName"
                />
              </Box>
              <Box sx={style.inputBoxes}>
                <Typography sx={style.inputText}> Payroll Frequency</Typography>
                <CustomInput
                  variant="outlined"
                  required
                  fullWidth
                  id="employeeNumber"
                  error={employeeNumber?.error}
                  name="employeeNumber"
                  defaultValue={salaryData?.frequency}
                  readOnly={true}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  autoComplete="employeeNumber"
                />
              </Box>
              <Box sx={style.inputBoxes}></Box>
            </Box>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={12} lg={7.4}>
            <Grid container>
              <Grid item xs={12} lg={12} sx={style.gridbox}>
                <>
                  <Box sx={style.lineTextGrid}>
                    <Box sx={style.permissionLine}></Box>
                    <Box>
                      <Typography sx={style.gridText}>Salary</Typography>
                    </Box>
                  </Box>

                  <Box sx={style.inputSection}>
                    <Box sx={style.inputBox}>
                      <Typography sx={style.inputText}>Salary</Typography>
                      {salaryData?.isShowEmpty ||
                        salaryData?.employee?.salaryInfo?.salary !== 0 ? (
                        <CustomInput
                          variant="outlined"
                          required
                          fullWidth
                          id="salaryPHr"
                          readOnly={true}
                          error={salaryPHr?.error}
                          name="salaryPHr"
                          defaultValue={salaryData?.employee?.salaryInfo?.salary ?? 0}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          autoComplete="salaryPHr"
                        />
                      ) : (
                        <Typography sx={style.noDataText}>No Data</Typography>
                      )}
                    </Box>
                    <Box sx={style.inputBox}>
                      <Typography sx={style.inputText}>
                        Restraint of trade
                      </Typography>
                      {salaryData?.isShowEmpty ||
                        salaryData?.employee?.salaryInfo?.restraintTrade !== 0 ? (
                        <CustomInput
                          variant="outlined"
                          required
                          fullWidth
                          readOnly={true}
                          id="restraintTrade"
                          error={restraintTrade?.error}
                          name="restraintTrade"
                          defaultValue={
                            salaryData?.employee?.salaryInfo?.restraintTrade ?? 0
                          }
                          onChange={handleChange}
                          onFocus={handleFocus}
                          autoComplete="restraintTrade"
                        />
                      ) : (
                        <Typography sx={style.noDataText}>No Data</Typography>
                      )}
                    </Box>
                    <Box sx={style.inputBox}>
                      <Typography sx={style.inputText}>Start</Typography>
                      <TextField
                        fullWidth
                        readOnly
                        value={
                          salaryData?.from
                            ? moment.utc(salaryData.from).format(getDateFormat())
                            : ""
                        }
                        sx={style.dateInput}
                        slotProps={{
                          field: {
                            readOnly: true,
                          },
                        }}
                      />
                    </Box>
                    <Box sx={style.inputBox}>
                      <Typography sx={style.inputText}>End</Typography>
                      <TextField
                        readOnly
                        fullWidth
                        value={
                          salaryData?.to
                            ? moment.utc(salaryData.to).format(getDateFormat())
                            : ""
                        }
                        sx={style.dateInput}
                        slotProps={{
                          field: {
                            readOnly: true,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </>
                {salaryData?.employee?.salaryInfo?.hRate === "Hourly" && (
                  <Box sx={{ ...style.inputSection1, paddingTop: "15px" }}>
                    <Box sx={style.inputBoxRate}>
                      <Typography sx={style.inputText}>Working Rate</Typography>
                      <Box sx={style.inputBox}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          // type="Number"
                          name="reimbursiveTravel"
                          // onChange={(e) => { handleChangesWorking(e?.target?.value) }}
                          // value={travelValueWorking}
                          value={salaryData?.rate ?? 0}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ paddingTop: "35px" }}>
                      <Box sx={style.crossIcon}>X</Box>
                    </Box>

                    <Box sx={style.inputBoxRate}>
                      <Typography sx={style.inputText}>
                        Working Hours
                      </Typography>
                      <Box sx={style.inputBox}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          disabled
                          id="reimbursiveRate"
                          error={reimbursiveRate?.error}
                          readOnly={true}
                          // type="Number"
                          name="reimbursiveRate"
                          onChange={handleChangesWorking}
                          onFocus={handleFocus}
                          value={salaryData?.normalHours ?? 0}
                          autoComplete="reimbursiveRate"
                          InputProps={{
                            style: {
                              color: "black", // Text color for disabled state
                            },
                          }}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                              color: "black", // Fallback for other browsers
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ paddingTop: "35px" }}>
                      <Box sx={style.crossIcon}>=</Box>
                    </Box>

                    <Box sx={style.inputBoxRate}>
                      <Typography sx={style.inputText}>
                        Total Working Amount
                      </Typography>
                      <Box sx={style.inputBox}>
                        <TextField
                          type="Number"
                          name="totalTravel"
                          value={calculatedTotalWorking}
                          // defaultValue="0"
                          disabled
                          defaultValue={salaryData?.calHourlySalary}
                          InputProps={{
                            style: {
                              color: "black", // Text color for disabled state
                            },
                          }}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                              color: "black", // Fallback for other browsers
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box sx={style.inputSection}>
                  <Box sx={style.inputBoxRate}>
                    <Typography sx={style.inputText}>
                      Travel Allowance (Business km x Rate)
                    </Typography>
                    <Box sx={style.inputBox}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        type="Number"
                        name="reimbursiveTravel"
                        placeholder={salaryData?.travelAllowanceKilometer}
                        onChange={(e) => {
                          handleChanges(e?.target?.value);
                        }}
                        value={travelValue}
                        defaultValue={salaryData?.travelAllowanceKilometer}
                        InputProps={{
                          inputProps: {
                            style: { color: "black" }, // Ensures input text is black
                          },
                        }}
                        sx={{
                          "& input::placeholder": {
                            color: "black", // Placeholder color
                            opacity: 1,
                          },
                        }}
                      />
                      <Box sx={style.crossIcon}>X</Box>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="reimbursiveRate"
                        error={reimbursiveRate?.error}
                        readOnly={true}
                        type="Number"
                        name="reimbursiveRate"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={
                          salaryData?.travelAllowanceRate ?? 0
                        }
                        autoComplete="reimbursiveRate"
                      />
                      <Box sx={style.crossIcon}>=</Box>
                      <TextField
                        type="Number"
                        name="totalTravel"
                        value={salaryData?.travelAllowanceAmount}
                        defaultValue={salaryData?.travelAllowanceAmount ?? 0}
                        disabled
                        // defaultValue={salaryData?.totalRemb }
                        InputProps={{
                          style: {
                            color: "black", // Text color for disabled state
                          },
                        }}
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                            color: "black", // Fallback for other browsers
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={style.inputSection}>
                  <Box sx={style.inputBoxRate}>
                    <Typography sx={style.inputText}>
                      Reimbursive travel (Business km x Rate)
                    </Typography>
                    <Box sx={style.inputBox}>
                      <TextField
                        variant="outlined"
                        required
                        fullWidth
                        type="Number"
                        name="reimbursiveTravel"
                        placeholder={salaryData?.travelReimbursementKilometer}
                        onChange={(e) => {
                          handleChanges(e?.target?.value);
                        }}
                        value={travelReimbursementValue}
                        defaultValue={salaryData?.travelReimbursementKilometer}
                        InputProps={{
                          inputProps: {
                            style: { color: "black" }, // Ensures input text is black
                          },
                        }}
                        sx={{
                          "& input::placeholder": {
                            color: "black", // Placeholder color
                            opacity: 1,
                          },
                        }}
                      />
                      <Box sx={style.crossIcon}>X</Box>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="reimbursiveRate"
                        error={reimbursiveRate?.error}
                        readOnly={true}
                        type="Number"
                        name="reimbursiveRate"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={
                          salaryData?.travelReimbursementRate ?? 0
                        }
                        autoComplete="reimbursiveRate"
                      />
                      <Box sx={style.crossIcon}>=</Box>
                      <TextField
                        type="Number"
                        name="totalTravel"
                        value={salaryData?.travelReimbursementAmount}
                        defaultValue={salaryData?.travelReimbursementAmount ?? 0}
                        disabled
                        // defaultValue={salaryData?.totalRemb }
                        InputProps={{
                          style: {
                            color: "black", // Text color for disabled state
                          },
                        }}
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                            color: "black", // Fallback for other browsers
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {uifSetting && salaryData?.uif > 0 && (
                  <>
                    <Box sx={style.greyLine}></Box>

                    <Box sx={style.lineTextGrid}>
                      <Box sx={style.permissionLine}></Box>
                      <Box>
                        <Typography sx={style.gridText}>
                          {" "}
                          {getTaxLabel(uifSetting)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={style.inputSection}>
                      <Box sx={style.inputBox}>
                        <Typography sx={style.inputText}>
                          {" "}
                          {getTaxLabel(uifSetting)}
                        </Typography>
                        <CustomInput
                          variant="outlined"
                          required
                          fullWidth
                          id="uif"
                          error={uif?.error}
                          name="uif"
                          onChange={handleChange}
                          onFocus={handleFocus}
                          defaultValue={salaryData?.uif ?? 0}
                          // readOnly={true}
                          value={uif?.value}
                          autoComplete="uif"
                        />
                      </Box>
                    </Box>
                  </>
                )}

                {sdlSetting && salaryData?.sdl > 0 && (
                  <>
                    <Box sx={style.greyLine}></Box>

                    <Box sx={style.lineTextGrid}>
                      <Box sx={style.permissionLine}></Box>
                      <Box>
                        <Typography sx={style.gridText}>
                          {getTaxLabel(sdlSetting)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={style.inputSection}>
                      <Box sx={style.inputBox}>
                        <Typography sx={style.inputText}>
                          {getTaxLabel(sdlSetting)}
                        </Typography>
                        <CustomInput
                          variant="outlined"
                          required
                          fullWidth
                          id="sdl"
                          error={uif?.error}
                          name="sdl"
                          onChange={handleChange}
                          onFocus={handleFocus}
                          defaultValue={salaryData?.sdl ?? 0}
                          readOnly={true}
                          autoComplete="uif"
                        />
                      </Box>
                    </Box>
                  </>
                )}

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Tax</Typography>
                  </Box>
                </Box>

                <Box sx={style.inputSection}>
                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>TaxableIncome</Typography>
                    {salaryData?.isShowEmpty || salaryData?.taxableIncome !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="taxable"
                        error={taxable?.error}
                        name="taxable"
                        readOnly={true}
                        defaultValue={salaryData?.taxableIncome ?? 0}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        autoComplete="taxable"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>

                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>Total Tax</Typography>
                    {salaryData?.isShowEmpty || salaryData?.totalTax !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="totalTax"
                        error={totalTax?.error}
                        type="Number"
                        name="totalTax"
                        defaultValue={salaryData?.totalTax ?? 0}
                        readOnly={true}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        autoComplete="totalTax"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>

                  {/* <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>Net Tax</Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      id="netTax"
                      error={totalTax?.error}
                      type="Number"
                      name="netTax"
                      defaultValue={salaryData?.totalTax ?? 0}
                      value={netTax?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      autoComplete="totalTax" />
                  </Box> */}
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Overtime</Typography>
                  </Box>
                </Box>

                <Box sx={style.inputSection}>
                  <Box sx={style.inputBox}>
                    {/* {salaryData?.employee?.salaryInfo?.hRate === "Salary" && (
                      <>
                        <Typography sx={style.inputText}>Overtime</Typography>
                        {salaryData?.isShowEmpty || salaryData?.overTime !== 0 ? (

                          <CustomInput
                            variant="outlined"
                            required
                            fullWidth
                            id="overtime"
                            error={overtime?.error}
                            name="overtime"
                            readOnly={true}
                            defaultValue={salaryData?.overTime ?? 0}
                            onChange={handleChange}
                            onFocus={handleFocus}

                            autoComplete="taxable" />
                        ) : (
                          <Typography sx={style.noDataText}>No Data</Typography>
                        )}
                      </>
                    )} */}
                  </Box>
                  {
                    <Box sx={style.inputSection1}>
                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          OverTime Rates
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            fullWidth
                            // type="Number"
                            name="reimbursiveTravel"
                            // onChange={(e) => { handleChanges(e?.target?.value) }}
                            // value={travelValue}
                            value={
                              salaryData?.employee?.additionalInformation
                                ?.overTimeRate ?? 0
                            }
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>X</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>Rate</Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            disabled
                            fullWidth
                            id="reimbursiveRate"
                            error={reimbursiveRate?.error}
                            readOnly={true}
                            // type="Number"
                            name="reimbursiveRate"
                            onChange={handleChange}
                            // onFocus={handleFocus}
                            value={salaryData?.rate ?? 0}
                            autoComplete="reimbursiveRate"
                            InputProps={{
                              style: {
                                color: "black",
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black",
                                color: "black",
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>X</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          OverTime Hours
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            disabled
                            fullWidth
                            id="reimbursiveRate"
                            error={reimbursiveRate?.error}
                            readOnly={true}
                            // type="Number"
                            name="reimbursiveRate"
                            onChange={handleChange}
                            // onFocus={handleFocus}
                            value={salaryData?.overtimeHours ?? 0}
                            autoComplete="reimbursiveRate"
                            InputProps={{
                              style: {
                                color: "black", // Text color for disabled state
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                                color: "black", // Fallback for other browsers
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>=</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Total OverTime
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            type="Number"
                            name="totalTravel"
                            value={calculatedTotalWorking}
                            // defaultValue="0"
                            disabled
                            defaultValue={salaryData?.overTime}
                            InputProps={{
                              style: {
                                color: "black", // Text color for disabled state
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                                color: "black", // Fallback for other browsers
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  }

                  <Box sx={style.inputBox}>
                    {/* {salaryData?.employee?.salaryInfo?.hRate === "Salary" && (
                      <>
                        <Typography sx={style.inputText}>Double Overtime</Typography>

                        {salaryData?.isShowEmpty || salaryData?.doubleOverTime !== 0 ? (

                          <CustomInput
                            variant="outlined"
                            required
                            fullWidth
                            id="doubleOvertime"
                            error={overtime?.error}
                            type="Number"
                            name="doubleOvertime"
                            defaultValue={salaryData?.doubleOverTime ?? 0}
                            readOnly={true}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            autoComplete="totalTax" />
                        ) : (
                          <Typography sx={style.noDataText}>No Data</Typography>
                        )}
                      </>
                    )} */}
                  </Box>
                  {
                    <Box sx={style.inputSection1}>
                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Double Overtime Rates
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            fullWidth
                            // type="Number"
                            name="reimbursiveTravel"
                            // onChange={(e) => { handleChanges(e?.target?.value) }}
                            // value={travelValue}
                            value={
                              salaryData?.employee?.additionalInformation
                                ?.doubleOverTimeRate ?? 0
                            }
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>X</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>Rate</Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            disabled
                            fullWidth
                            id="reimbursiveRate"
                            error={reimbursiveRate?.error}
                            readOnly={true}
                            // type="Number"
                            name="reimbursiveRate"
                            onChange={handleChange}
                            // onFocus={handleFocus}
                            value={salaryData?.rate ?? 0}
                            autoComplete="reimbursiveRate"
                            InputProps={{
                              style: {
                                color: "black",
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black",
                                color: "black",
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>X</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Double Overtime Hours
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            disabled
                            fullWidth
                            id="reimbursiveRate"
                            error={reimbursiveRate?.error}
                            readOnly={true}
                            // type="Number"
                            name="reimbursiveRate"
                            onChange={handleChange}
                            // onFocus={handleFocus}
                            value={salaryData?.doubleOvertimeHours ?? 0}
                            autoComplete="reimbursiveRate"
                            InputProps={{
                              style: {
                                color: "black", // Text color for disabled state
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                                color: "black", // Fallback for other browsers
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>=</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Total Double Overtime
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            type="Number"
                            name="totalTravel"
                            value={calculatedTotalWorking}
                            // defaultValue="0"
                            disabled
                            defaultValue={salaryData?.doubleOverTime}
                            InputProps={{
                              style: {
                                color: "black", // Text color for disabled state
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                                color: "black", // Fallback for other browsers
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  }

                  <Box sx={style.inputBox}>
                    {/* {salaryData?.employee?.salaryInfo?.hRate === "Salary" && (
                      <>
                        <Typography sx={style.inputText}>Extratime</Typography>

                        {salaryData?.isShowEmpty || salaryData?.extraTime !== 0 ? (

                          <CustomInput
                            variant="outlined"
                            required
                            fullWidth
                            id="doubleOvertime"
                            error={overtime?.error}
                            type="Number"
                            name="doubleOvertime"
                            defaultValue={salaryData?.extraTime ?? 0}
                            readOnly={true}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            autoComplete="totalTax" />
                        ) : (
                          <Typography sx={style.noDataText}>No Data</Typography>
                        )}
                      </>
                    )} */}
                  </Box>
                  {
                    <Box sx={style.inputSection1}>
                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Extratime Rates
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            fullWidth
                            // type="Number"
                            name="reimbursiveTravel"
                            // onChange={(e) => { handleChanges(e?.target?.value) }}
                            // value={travelValue}
                            value={
                              salaryData?.employee?.additionalInformation
                                ?.extraTimeRate ?? 0
                            }
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>X</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>Rate</Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            disabled
                            fullWidth
                            id="reimbursiveRate"
                            error={reimbursiveRate?.error}
                            readOnly={true}
                            // type="Number"
                            name="reimbursiveRate"
                            onChange={handleChange}
                            // onFocus={handleFocus}
                            value={salaryData?.rate ?? 0}
                            autoComplete="reimbursiveRate"
                            InputProps={{
                              style: {
                                color: "black",
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black",
                                color: "black",
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>X</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Extratime Hours
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            variant="outlined"
                            required
                            disabled
                            fullWidth
                            id="reimbursiveRate"
                            error={reimbursiveRate?.error}
                            readOnly={true}
                            // type="Number"
                            name="reimbursiveRate"
                            onChange={handleChange}
                            // onFocus={handleFocus}
                            value={salaryData?.extratimeHours ?? 0}
                            autoComplete="reimbursiveRate"
                            InputProps={{
                              style: {
                                color: "black", // Text color for disabled state
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                                color: "black", // Fallback for other browsers
                              },
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ paddingTop: "35px" }}>
                        <Box sx={style.crossIcon}>=</Box>
                      </Box>

                      <Box sx={style.inputBoxRate}>
                        <Typography sx={style.inputText}>
                          Total Extratime
                        </Typography>
                        <Box sx={style.inputBox}>
                          <TextField
                            type="Number"
                            name="totalTravel"
                            value={calculatedTotalWorking}
                            // defaultValue="0"
                            disabled
                            defaultValue={salaryData?.extraTime}
                            InputProps={{
                              style: {
                                color: "black", // Text color for disabled state
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "black", // For Chrome and WebKit-based browsers
                                color: "black", // Fallback for other browsers
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  }

                  {/* {salaryData?.rate > 0 && (

                    <Box sx={style.inputBox}>
                      <Typography sx={style.inputText}>Rate</Typography>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="rate"
                        error={rate?.error}
                        type="Number"
                        name="rate"
                        defaultValue={salaryData?.rate ?? 0}
                        readOnly={true}

                        onChange={handleChange}
                        onFocus={handleFocus}
                        autoComplete="totalTax" />
                    </Box>
                  )} */}
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Nett Pay </Typography>
                  </Box>
                </Box>

                <Box sx={style.inputSection}>
                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>Nett Pay</Typography>
                    {salaryData?.isShowEmpty || salaryData?.netPay !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="nettPay"
                        error={nettPay?.error}
                        name="nettPay"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.netPay || 0}
                        readOnly={true}
                        autoComplete="nettPay"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>

                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>Payable</Typography>
                    {salaryData?.isShowEmpty || salaryData?.payable !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="payable"
                        error={payable?.error}
                        name="payable"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.payable ?? 0}
                        readOnly={true}
                        autoComplete="payable"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Absent</Typography>
                  </Box>
                </Box>
                <Box sx={style.inputSection}>
                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>Absent</Typography>
                    {salaryData?.isShowEmpty || salaryData?.netPay !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="nettPay"
                        error={nettPay?.error}
                        name="nettPay"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.attendanceDeduction}
                        readOnly={true}
                        autoComplete="nettPay"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Income</Typography>
                  </Box>
                </Box>

                <Box sx={style.inputSection}>
                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>Gross Income</Typography>
                    {salaryData?.isShowEmpty || salaryData?.grossInc !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="grossIncome"
                        error={grossIncome?.error}
                        name="grossIncome"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.grossInc ?? 0}
                        readOnly={true}
                        autoComplete="grossIncome"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>

                  <Box sx={style.inputBox}>
                    <Typography sx={style.inputText}>
                      Total Deductions
                    </Typography>
                    {salaryData?.isShowEmpty || salaryData?.allDeduction !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="totalDeductions"
                        error={totalDeductions?.error}
                        name="totalDeductions"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.allDeduction ?? 0}
                        readOnly={true}
                        autoComplete="totalDeductions"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4.4}>
            <Grid container>
              <Grid item xs={12} lg={12} sx={style.gridbox}>
                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Deductions</Typography>
                  </Box>
                </Box>
                <Box sx={style.inputSectionFull}>
                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Deductions</Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      id="allDeductions"
                      error={allDeductions?.error}
                      name="allDeductions"
                      onChange={handleChange}
                      onFocus={handleFocus}
                      buttonText="View/Adjust"
                      readOnly={true}
                      handleView={handleDeduction}
                      defaultValue={salaryData?.totalDeductions ?? 0}
                      autoComplete="allDeductions"
                    />
                  </Box>

                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Loan repayment</Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      id="loanRepayment"
                      error={loanRepayment?.error}
                      name="loanRepayment"
                      onChange={handleChange}
                      onFocus={handleFocus}
                      buttonText="View/Adjust"
                      readOnly={true}
                      handleView={handleLoan}
                      defaultValue={salaryData?.totalLoan ?? 0}
                      autoComplete="loanRepayment"
                    />
                  </Box>
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Fringe Benefit </Typography>
                  </Box>
                </Box>
                <Box sx={style.inputSectionFull}>
                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Fringe Benefit</Typography>
                    {salaryData?.isShowEmpty || salaryData?.totalFringe !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="fringeBenefit"
                        error={fringeBenefit?.error}
                        name="fringeBenefit"
                        onChange={handleChange}
                        buttonText="View"
                        handleView={handleFringe}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.totalFringe ?? 0}
                        readOnly={true}
                        autoComplete="fringeBenefit"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Allowance</Typography>
                  </Box>
                </Box>
                <Box sx={style.inputSectionFull}>
                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Allowances</Typography>
                    {salaryData?.isShowEmpty || salaryData?.totalAllowance !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="allowances"
                        readOnly={true}
                        error={allowances?.error}
                        name="allowances"
                        onChange={handleChange}
                        buttonText="View"
                        onFocus={handleFocus}
                        defaultValue={salaryData?.totalAllowance ?? 0}
                        handleView={handleAllowance}
                        autoComplete="allowances"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>

                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Commission</Typography>
                    {salaryData?.isShowEmpty || salaryData?.commission !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="commission"
                        error={commission?.error}
                        type="Number"
                        value={commission?.value}
                        name="commission"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        defaultValue={salaryData?.commission ?? 0}
                        autoComplete="commission"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={style.greyLine}></Box>
                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Contribution</Typography>
                  </Box>
                </Box>
                <Box sx={style.inputSectionFull}>
                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Contribution</Typography>
                    {salaryData?.isShowEmpty || salaryData?.totalContribution !== 0 ? (
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        readOnly={true}
                        id="contributionCompany"
                        error={commission?.error}
                        name="contributionCompany"
                        onChange={handleChange}
                        onFocus={handleFocus}
                        buttonText="View"
                        defaultValue={salaryData?.totalContribution ?? 0}
                        handleView={handlePension}
                        autoComplete="contributionCompany"
                      />
                    ) : (
                      <Typography sx={style.noDataText}>No Data</Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Levies</Typography>
                  </Box>
                </Box>

                <Box sx={style.inputSectionFull}>
                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>
                      Employer Levies
                    </Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      id="employerLevies"
                      error={employerLevies?.error}
                      name="employerLevies"
                      onChange={handleChange}
                      onFocus={handleFocus}
                      buttonText="View/Adjust"
                      readOnly={true}
                      handleView={handleLevie}
                      defaultValue={salaryData?.totalLevie ?? 0}
                      autoComplete="employerLevies"
                    />
                  </Box>
                </Box>

                <Box sx={style.greyLine}></Box>

                <Box sx={style.lineTextGrid}>
                  <Box sx={style.permissionLine}></Box>
                  <Box>
                    <Typography sx={style.gridText}>Leave</Typography>
                  </Box>
                </Box>

                <Box sx={style.inputSectionFull}>
                  <Box sx={style.inputBoxfullWidth}>
                    <Typography sx={style.inputText}>Leave</Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      id="leave"
                      error={leave?.error}
                      name="leave"
                      onChange={handleChange}
                      onFocus={handleFocus}
                      buttonText="leave"
                      defaultValue={salaryData?.totalLeave ?? 0}
                      readOnly={true}
                      handleView={handleLeave}
                      autoComplete="leave"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <LoadingButton
              loading={loading}
              variant="contained"
              type="submit"
              color="success"
              sx={style.jobButtons}
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      </form>

      <Modal
        open={pensionOpen}
        setOpen={setPensionOpen}
        title={"Contribution"}
        loading={loading}
      >
        <Box>
          {/* <Grid container sx={style.gridContainer}>
            <Grid item xs={12} sm={5.9}>
              <InputLabel sx={style.labels}>Type</InputLabel>
              <TextField
                name="type"
                value={typePension}
                onChange={(e) => { setTypePension(e.target.value) }}
                sx={style.dropdowns}
              />

            </Grid>

            <Grid item xs={12} sm={5.9}>
              <InputLabel sx={style.labels}>Amount</InputLabel>
              <TextField
                // label={"Reason"}
                name="amount"
                type="number"
                value={amountPension}
                onChange={(e) => { setAmountPension(e.target.value) }}
                sx={style.dropdowns}
              />
            </Grid>

            <Button variant="contained" type="button" color="success" sx={style.addButton} onClick={handlePensionData}>Add</Button>

          </Grid> */}
          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Contribution Type</TableCell>
                  <TableCell sx={style.TableHead}>Employee RateValue</TableCell>
                  <TableCell sx={style.TableHead}>Employer RateValue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contribution?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h6">No Data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contribution?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={style.tabledata}>{item?.type}</TableCell>
                      <TableCell sx={style.tabledata}>
                        {item?.employeeCont?.totalAmount}
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        {item?.employerCont?.totalAmount}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
      <Modal
        open={allowanceOpen}
        setOpen={setAllowanceOpen}
        title={"Allowance"}
        loading={loading}
      >
        <Box>
          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Type</TableCell>
                  <TableCell sx={style.TableHead}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allowanceData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h6">No Data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allowanceData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={style.tabledata}>{item?.type}</TableCell>
                      <TableCell sx={style.tabledata}>
                        {item?.totalAmount}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
      <Modal
        open={levieOpen}
        setOpen={setLevieOpen}
        title={"Levie"}
        loading={loading}
        btnText={"Add"}
        handleCheck={handleLevieSubmit}
        disabled={
          levies.some(
            (item) => !item.type || !item.rateValue || Number(item.rateValue) <= 0
          )
        }
      >
        <Box>
          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Type</TableCell>
                  <TableCell sx={style.TableHead}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(levies) && levies.length > 0
                  ? levies
                  : [{ type: "", rateValue: "" }]
                ).map((item, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell sx={style.tabledata}>
                        <Select
                          value={item?.type || ""}
                          onChange={(e) => handleLevieChange(e.target.value, index, "type")}
                          name="type"
                          sx={style.hoursInput}
                          displayEmpty
                        >
                          <MenuItem disabled value="">
                            Select Type
                          </MenuItem>
                          {leviesType.map((items, index) => (
                            <MenuItem sx={style.dropDownMenu} key={index} value={items?.name}>
                              {items?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        <TextField
                          name="rateValue"
                          type="number"
                          value={
                            item?.rateValue
                            ?? item?.employerCont?.employerContAmount
                            ?? ""
                          }
                          onChange={(e) => handleLevieChange(e.target.value, index, "rateValue")}
                          sx={style.dropdowns}
                          placeholder="Enter Amount"
                          inputProps={{
                            min: 0,
                            step: "0.01",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
      <Modal
        open={fringeOpen}
        setOpen={setFringeOpen}
        title={"Fringe"}
        loading={loading}
      >
        <Box>
          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Type</TableCell>
                  <TableCell sx={style.TableHead}>Employer fringe</TableCell>
                  <TableCell sx={style.TableHead}>Employee fringe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fringeData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h6">No Data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  fringeData?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={style.tabledata}>{item?.type}</TableCell>
                      <TableCell sx={style.tabledata}>
                        {item?.employerCont?.totalAmount}
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        {item?.employeeCont?.totalAmount}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
      <Modal
        open={loanOpen}
        setOpen={setLoanOpen}
        title={"Loan Repayment"}
        loading={loading}
        btnText={"Save"}
        handleCheck={handleLoanSubmit}
        disabled={
          !hasLoanChanges || // This is the key condition - no changes
          loans.length === 0 ||
          loans.some(
            (item) =>
              // item.repayAmt === 0 ||
              item.adjustedValue === "" ||
              Number(item.adjustedValue) < 1 ||
              Number(item.adjustedValue) > Number(item.loanAmt)
          )
        }
      >
        <Box>
          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Loan Amount</TableCell>
                  <TableCell sx={style.TableHead}>Repayment Amount</TableCell>
                  <TableCell sx={style.TableHead}>Adjusted Value</TableCell>
                  <TableCell sx={style.TableHead}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(loans) && loans.length > 0
                  ? loans
                  : [{ loanAmt: 0, repayAmt: 0, adjustedValue: 0 }]
                ).map((item, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell sx={style.tabledata}>
                        <TextField
                          name="loanAmount"
                          value={item.loanAmt || ""}
                          sx={style.dropdowns}
                          type="number"
                          inputProps={{ readOnly: true }}
                          disabled={item.repayAmt === 0 ? true : false}
                        />
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        <TextField
                          name="repayAmt"
                          value={item.repayAmt || "0"}
                          sx={style.dropdowns}
                          inputProps={{ readOnly: true }}
                          disabled={item.repayAmt === 0 ? true : false}
                        />
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        <TextField
                          name="adjustedValue"
                          value={item.adjustedValue ?? item.repayAmt ?? ""}
                          onChange={(e) => handleLoanChange(e.target.value, index, "adjustedValue")}
                          inputProps={{ min: 1, max: item.loanAmt, type: "number" }}
                          sx={{ ...style.dropdowns, }}
                          disabled={item.repayAmt === 0 ? true : false}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
      <Modal
        open={deductionOpen}
        setOpen={setDeductionOpen}
        title={"All Deductions"}
        loading={loading}
        btnText={"Add"}
        handleCheck={() => updateDeduction()}
        disabled={deduction.some(
          (item) =>
            item.rateValue === "" ||
            Number(item.rateValue) < 0
        )}
      >
        <Box>
          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Type</TableCell>
                  <TableCell sx={style.TableHead}>Amount</TableCell>
                  <TableCell sx={style.TableHead}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(deduction) && deduction.length > 0
                  ? deduction
                  : [{ type: "", valueType: "" }]
                ).map((item, index) => {
                  const isExisting = !!item._id;
                  return (<TableRow key={index}>
                    <TableCell sx={style.tabledata}>
                      <Select value={item?.type || ""} onChange={(e) => handleDeductionChange(e.target.value, index, "type")} name="type" sx={style.hoursInput} displayEmpty disabled={isExisting}>
                        <MenuItem disabled value="">
                          Select Type
                        </MenuItem>
                        {deductionType.map((items, index) => (<MenuItem sx={style.dropDownMenu} key={index} value={items?.name}>
                          {items?.name}
                        </MenuItem>))}
                      </Select>
                    </TableCell>
                    <TableCell sx={style.tabledata}>
                      <TextField name="rateValue" value={item.rateValue || ""} onChange={(e) => handleDeductionChange(e.target.value, index, "rateValue")} sx={style.dropdowns} disabled={isExisting} />
                    </TableCell>
                    <TableCell sx={style.tabledata}>
                      <Box sx={style.btnGridAction}>
                        {index === 0 ? (<Box sx={style.addIcon} onClick={() => handleAddDeduction()}>
                          +
                        </Box>) : (<Box sx={style.deleteIcon} onClick={() => handleDeleteDeduction(index)}>
                          <DeleteOutlineIcon />
                        </Box>)}
                      </Box>
                    </TableCell>
                  </TableRow>);
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>

      <Modal
        open={leaveOpen}
        setOpen={setLeaveOpen}
        title={"Leave"}
        loading={loading}
      >
        <Box>
          <Grid container sx={style.gridContainer}>
            <Grid item xs={12} sm={5.9}>
              <InputLabel sx={style.labels}>From</InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <DatePicker
                    value={leaveFrom}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                    sx={style.calenderInput}
                    onChange={(newValue) => {
                      setLeaveFrom(newValue);
                    }}
                    minDate={dayjs(salaryData?.from)}
                    maxDate={dayjs(salaryData?.to)}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={5.9}>
              <InputLabel sx={style.labels}>To</InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <DatePicker
                    value={leaveTo}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                    sx={style.calenderInput}
                    onChange={(newValue) => {
                      setLeaveTo(newValue);
                    }}
                    minDate={leaveFrom ? dayjs(leaveFrom) : dayjs(salaryData?.from)}
                    maxDate={dayjs(salaryData?.to)}
                  />
                </Box>
              </LocalizationProvider>{" "}
            </Grid>
            <Grid item xs={12} sm={5.9}>
              <InputLabel sx={style.labelType}>Leave Type</InputLabel>
              <Select
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
                sx={style.dropdownLeave}
                displayEmpty
              >
                <MenuItem disabled value="">
                  Select Type
                </MenuItem>
                {getLeaveType?.map((items, index) => (

                  <MenuItem sx={style.dropDownMenu} key={index} value={items?.type}>
                    {items?.type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Button
            variant="contained"
            type="button"
            color="success"
            sx={style.addButton}
            onClick={handleLeaveData}
            disabled={
              !leaveFrom || !leaveTo || !leaveType || dayjs(leaveTo).isBefore(dayjs(leaveFrom))
            }
          >
            Add
          </Button>

          <TableContainer sx={style.table}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={style.TableHead}>Leave Type</TableCell>
                  <TableCell sx={style.TableHead}>Leave From</TableCell>
                  <TableCell sx={style.TableHead}>Leave To</TableCell>
                  <TableCell sx={style.TableHead}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveData?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="h6">No Data</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  leaveData?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={style.tabledata}>
                        {item?.leaveType}
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        {formatDate(item?.leaveFrom)}
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        {formatDate(item?.leaveTo)}
                      </TableCell>
                      <TableCell sx={style.tabledata}>
                        {"Unpaid"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Modal>
    </>
  );
};
export default PayslipPage;
